// hooks/useDecibelAverage.ts
import { useEffect, useRef } from "react"
import { useParams } from "react-router-dom"

export const useDecibelAverage = (currentDecibel: number) => {
    const { sonometerId } = useParams<{ sonometerId: string }>()
    const screenId = sonometerId || "live-sound-display"
    const decibelValues = useRef<number[]>([])
    const isRecordingRef = useRef<boolean>(false)

    useEffect(() => {
        if (currentDecibel > 0) {
            decibelValues.current.push(currentDecibel)
            const average =
                decibelValues.current.reduce((sum, val) => sum + val, 0) /
                decibelValues.current.length

            const key = `sonometer-data-${screenId}`

            // Get existing data or create new
            const existingData = localStorage.getItem(key)
            const data = existingData
                ? JSON.parse(existingData)
                : {
                      history: [],
                      lastUpdated: Date.now(),
                  }

            // If this is the start of a new recording session, add to history
            if (!isRecordingRef.current) {
                isRecordingRef.current = true

                // Add new session entry to history
                data.history.push({
                    score: average,
                    timestamp: Date.now(),
                    sessionId: `session-${Date.now()}`,
                })
            } else {
                // Update the latest session entry with current average
                if (data.history.length > 0) {
                    data.history[data.history.length - 1].score = average
                }
            }

            data.lastUpdated = Date.now()

            // Store updated data
            localStorage.setItem(key, JSON.stringify(data))

            window.dispatchEvent(
                new CustomEvent("localStorageUpdated", {
                    detail: { key, value: data },
                })
            )
        }
    }, [currentDecibel, screenId])

    // Reset recording state when component unmounts or recording stops
    useEffect(() => {
        return () => {
            isRecordingRef.current = false
        }
    }, [])

    return decibelValues.current
}
